import { useState } from "react";
import { Check } from "lucide-react";
import { optionsForMarket, type SingleQuestionDefinition } from "@ferrivia/api-contract";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { TextInput, ToggleChip } from "@ferrivia/ui";
import { focusWithoutPan } from "../../onboarding/focusWithoutPan";
import c from "./ChoiceGroup.module.css";
import fs from "./MultiChoiceQuestion.module.css";
import f from "./fieldBox.module.css";
import s from "./SearchableMultiChoice.module.css";

export interface SearchableMultiChoiceProps {
  question: SingleQuestionDefinition;
  legend: string;
  value: readonly string[];
  onChange: (selected: string[]) => void;
  optionLabelFor: (optionId: string) => string;
}

// Same normalisation as SupplementPicker's, so the two search boxes match
// the same way.
function normalize(value: string): string {
  return value.toLowerCase().replace(/®/g, "").replace(/['’]/g, "");
}

/** `control: "searchable_multi_choice"`: a multi_choice question whose option
    list is too long to show as chips (`medical_conditions_details`). The
    selections sit above the search as checked chips -- unchecking one
    removes it -- and the search filters the options below.

    Options always render in definition order, which is editorial
    (alphabetical by fi label). The filter only hides; it never re-ranks,
    and there is no "common" section -- the same rule as SupplementPicker. */
export function SearchableMultiChoice({ question, legend, value, onChange, optionLabelFor }: SearchableMultiChoiceProps) {
  const { t } = useTranslation("client");
  const [query, setQuery] = useState("");
  const options = optionsForMarket(question.options ?? [], useMarket());

  function toggle(optionId: string) {
    onChange(value.includes(optionId) ? value.filter((id) => id !== optionId) : [...value, optionId]);
  }

  const selected = options.filter((option) => value.includes(option.id));
  const normalizedQuery = normalize(query.trim());
  const matches =
    normalizedQuery === ""
      ? options
      : options.filter((option) => normalize(optionLabelFor(option.id)).includes(normalizedQuery));

  return (
    <fieldset className={fs.fieldset}>
      <legend className={fs.legend}>{legend}</legend>
      {selected.length > 0 ? (
        <div className={c.chips}>
          {selected.map((option) => {
            const label = optionLabelFor(option.id);
            return (
              <ToggleChip
                key={option.id}
                className={c.chip}
                checked
                aria-label={t("questionnaire.conditionSearch.remove", "Poista {{label}}", { label })}
                onChange={() => toggle(option.id)}
              >
                {label}
              </ToggleChip>
            );
          })}
        </div>
      ) : null}
      <TextInput
        className={f.box}
        type="search"
        aria-label={legend}
        placeholder={t("questionnaire.conditionSearch.placeholder", "Hae sairautta")}
        value={query}
        onMouseDown={focusWithoutPan}
        onChange={(event) => setQuery(event.target.value)}
      />
      <p aria-live="polite" className={s.liveRegion}>
        {t("questionnaire.conditionSearch.resultsCount", "{{count}} hakutulosta", { count: matches.length })}
      </p>
      <div className={s.results}>
        {matches.length === 0 ? (
          <p className={s.empty}>
            {t("questionnaire.conditionSearch.noResults", "Ei osumia – kirjoita se alla olevaan Muu, mikä? -kenttään")}
          </p>
        ) : (
          matches.map((option) => {
            const checked = value.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                className={s.result}
                aria-pressed={checked}
                onClick={() => toggle(option.id)}
              >
                <span>{optionLabelFor(option.id)}</span>
                {checked ? <Check aria-hidden size={18} className={s.check} /> : null}
              </button>
            );
          })
        )}
      </div>
    </fieldset>
  );
}
